import React, { useState } from 'react';
import { Card, Button, Spinner, Alert, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { PlusIcon } from '@heroicons/react/solid';
import { toast } from 'react-toastify';
import {
  useFetchSharedAccessLinksQuery,
  useCreateSharedAccessLinkMutation,
  useUpdateSharedAccessLinkMutation,
  useDeleteSharedAccessLinkMutation,
  useDeactivateSharedAccessLinkMutation,
} from '#store';
import SharedAccessLinksList from '#components/shared-access/SharedAccessLinksList';
import SharedAccessLinkCreateModal from './SharedAccessLinkCreateModal';
import SharedAccessLinkEditModal from './SharedAccessLinkEditModal';
import SharedAccessLogsModal from './SharedAccessLogsModal';

const SharedAccessLinksPage = () => {
  const { t } = useTranslation();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [selectedLink, setSelectedLink] = useState(null);

  const { data: linksData, isLoading, error, isFetching } = useFetchSharedAccessLinksQuery();
  const [createLink, { isLoading: isCreating }] = useCreateSharedAccessLinkMutation();
  const [updateLink, { isLoading: isUpdating }] = useUpdateSharedAccessLinkMutation();
  const [deleteLink, { isLoading: isDeleting }] = useDeleteSharedAccessLinkMutation();
  const [deactivateLink, { isLoading: isDeactivating }] = useDeactivateSharedAccessLinkMutation();

  // Ensure links is always an array
  const links = Array.isArray(linksData) ? linksData : [];

  const handleCreate = async (data) => {
    try {
      await createLink(data).unwrap();
      setShowCreateModal(false);
      toast.success(t('shared-access.create-success'));
    } catch (err) {
      toast.error(err?.data?.detail || t('shared-access.create-error'));
    }
  };

  const handleEdit = (link) => {
    setSelectedLink(link);
    setShowEditModal(true);
  };

  const handleUpdate = async (data) => {
    try {
      await updateLink({ id: selectedLink.id, ...data }).unwrap();
      setShowEditModal(false);
      setSelectedLink(null);
      toast.success(t('shared-access.update-success'));
    } catch (err) {
      toast.error(err?.data?.detail || t('shared-access.update-error'));
    }
  };

  const handleDelete = async (link) => {
    const confirmed = window.confirm(t('shared-access.delete-confirm-soft-delete'));
    
    if (!confirmed) return;

    try {
      await deleteLink({ id: link.id }).unwrap();
      toast.success(t('shared-access.delete-success-soft-delete'));
    } catch (err) {
      toast.error(err?.data?.detail || t('shared-access.delete-error'));
    }
  };

  const handleDeactivate = async (link) => {
    if (window.confirm(t('shared-access.deactivate-confirm'))) {
      try {
        await deactivateLink({ id: link.id }).unwrap();
        toast.success(t('shared-access.deactivate-success'));
      } catch (err) {
        toast.error(err?.data?.detail || t('shared-access.deactivate-error'));
      }
    }
  };

  const handleViewLogs = (link) => {
    setSelectedLink(link);
    setShowLogsModal(true);
  };

  const handleCopyUrl = () => {
    // This is handled in SharedAccessLinkCard
  };

  // Show loading only on initial load, not during background refetch
  if (isLoading && !linksData) {
    return (
      <div className='d-flex justify-content-center align-items-center' style={{ minHeight: '400px' }}>
        <Spinner />
      </div>
    );
  }

  if (error && !linksData) {
    return (
      <Alert variant='danger'>
        {error?.data?.detail || t('shared-access.error-loading') || 'Error loading shared access links'}
      </Alert>
    );
  }

  return (
    <>
      <div className='d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4'>
        <div className='d-block'>
          <h4>{t('shared-access.title')}</h4>
          <p className='mb-0'>{t('shared-access.subtitle')}</p>
        </div>
      </div>

      <Row className='mb-3'>
        <Col>
          <Button variant='logo-green' onClick={() => setShowCreateModal(true)}>
            <PlusIcon className='icon icon-xs me-2' />
            {t('shared-access.create-link')}
          </Button>
        </Col>
      </Row>

      <Card border='0' className='table-wrapper shadow overflow-hidden'>
        <Card.Body>
          {links.length === 0 ? (
            <div className='text-center py-5'>
              <p className='text-muted'>{t('shared-access.no-links')}</p>
              <Button variant='logo-green' onClick={() => setShowCreateModal(true)}>
                <PlusIcon className='icon icon-xs me-2' />
                {t('shared-access.create-first-link')}
              </Button>
            </div>
          ) : (
            <SharedAccessLinksList
              links={links}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onDeactivate={handleDeactivate}
              onViewLogs={handleViewLogs}
              onCopyUrl={handleCopyUrl}
            />
          )}
        </Card.Body>
      </Card>

      <SharedAccessLinkCreateModal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        onCreate={handleCreate}
        isLoading={isCreating}
      />

      <SharedAccessLinkEditModal
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          setSelectedLink(null);
        }}
        onUpdate={handleUpdate}
        link={selectedLink}
        isLoading={isUpdating}
      />

      <SharedAccessLogsModal
        show={showLogsModal}
        onHide={() => {
          setShowLogsModal(false);
          setSelectedLink(null);
        }}
        link={selectedLink}
      />
    </>
  );
};

export default SharedAccessLinksPage;

